// NoticeSlot.tsx · v12.10.31 (#2266 left-menu notice slot above the user card - UI-PROTO-2266-ANNOUNCEMENTS v1.2 LOCKED 11/4) · 2026-11-05 · VoiceSpectrum AI
// One notice at a time by priority (needs action > maintenance > password / MFA > ticket > new version > announcement > holiday), changing
// every few minutes (T3 setting); slides / fades in 30-60 s after sign-in, never during page load; no blinking, no sound; "Reduce motion" = no
// movement. All calm white; amber only for Needs action. × closes it for that person (maintenance can't be closed while live). Small screens:
// a dot on the menu button (Shell). Holiday theme: a class on <body> (greeting / sidebar / page) - never the message drawer.
// GET /api/t2/notices · POST /api/t2/notices/{id}/dismiss (BUILD-12.10.31-CONTRACTS.md ## F2). Written in T3 › Announcements (F4).
import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import api, { ApiError } from '../lib/api';
import './notice-slot.css';

export type NoticeKind = 'needsAction' | 'maintenance' | 'security' | 'ticket' | 'version' | 'announcement' | 'holiday';
export interface Notice {
  id: string; kind: NoticeKind; title: string; text?: string; icon?: string;
  link?: { type: 'none' | 'app' | 'pub' | 'ext'; to?: string; label?: string } | null;
  dismissible: boolean;
}
export interface NoticesPayload {
  rotateMinutes?: number; firstDelaySeconds?: number;
  holidayTheme?: { key: string; effect: 'greeting' | 'sidebar' | 'page' } | null;
  notices: Notice[];
}
const ORDER: NoticeKind[] = ['needsAction', 'maintenance', 'security', 'ticket', 'version', 'announcement', 'holiday'];
const ICON: Record<NoticeKind, string> = { needsAction: '⚠', maintenance: '🛠', security: '🔒', ticket: '💬', version: '✨', announcement: '📣', holiday: '🎉' };

export function useNotices() {
  return useQuery<NoticesPayload | null>({
    queryKey: ['t2-notices'],
    queryFn: async () => { try { return await api.get<NoticesPayload>('/t2/notices'); } catch (e) { if (e instanceof ApiError && (e.status === 404 || e.status === 403)) return null; throw e; } },
    staleTime: 5 * 60_000, refetchInterval: 15 * 60_000, retry: false,
  });
}

export default function NoticeSlot({ onCount }: { onCount?: (n: number) => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data } = useNotices();
  const [ready, setReady] = useState(false);
  const [idx, setIdx] = useState(0);
  const [gone, setGone] = useState<Set<string>>(new Set());
  const list = useMemo(() => (data?.notices ?? []).filter((n) => !gone.has(n.id))
    .sort((a, b) => ORDER.indexOf(a.kind) - ORDER.indexOf(b.kind)), [data, gone]);
  const reduce = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  // 30-60 s after sign-in (the T3 setting), never during page load
  useEffect(() => {
    const ms = Math.max(0, (data?.firstDelaySeconds ?? 30) * 1000);
    const t = window.setTimeout(() => setReady(true), ms);
    return () => window.clearTimeout(t);
  }, [data?.firstDelaySeconds]);
  // rotate every N minutes
  useEffect(() => {
    if (!ready || list.length < 2) return;
    const t = window.setInterval(() => setIdx((i) => i + 1), Math.max(1, data?.rotateMinutes ?? 5) * 60_000);
    return () => window.clearInterval(t);
  }, [ready, list.length, data?.rotateMinutes]);
  useEffect(() => { onCount?.(ready ? list.length : 0); }, [ready, list.length]); // eslint-disable-line react-hooks/exhaustive-deps
  // holiday theme on <body> - CSS keeps it off the message drawer
  useEffect(() => {
    const h = data?.holidayTheme;
    const cls = h ? [`vs-holiday-${h.key}`, `vs-holiday-${h.effect}`] : [];
    cls.forEach((c) => document.body.classList.add(c));
    return () => cls.forEach((c) => document.body.classList.remove(c));
  }, [data?.holidayTheme?.key, data?.holidayTheme?.effect]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!ready || list.length === 0) return null;
  const n = list[idx % list.length];
  const open = () => {
    const l = n.link;
    if (!l || l.type === 'none' || !l.to) return;
    if (l.type === 'ext') window.open(l.to, '_blank', 'noopener');
    else navigate(l.to);   // 'app' = an app route; 'pub' = the Help desk frame route of a page published in T3
  };
  const dismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    setGone((g) => new Set(g).add(n.id)); setIdx(0);
    api.post(`/t2/notices/${encodeURIComponent(n.id)}/dismiss`, {}).catch(() => { /* stays closed on this screen; the server keeps it next time */ })
      .finally(() => qc.invalidateQueries({ queryKey: ['t2-notices'] }));
  };
  const canClose = n.dismissible && n.kind !== 'maintenance';
  return (
    <div key={n.id + idx} className={`vs-notice k-${n.kind}${reduce ? ' no-motion' : ''}`} role="status" aria-live="polite"
      onClick={open} style={n.link && n.link.type !== 'none' ? { cursor: 'pointer' } : undefined} data-testid="notice-slot">
      <div className="vs-notice-t"><span aria-hidden="true">{n.icon || ICON[n.kind]}</span> {n.title}</div>
      {canClose && <button type="button" className="vs-notice-x" title="Close" aria-label="Close this notice" onClick={dismiss}>×</button>}
      {n.text && <div className="vs-notice-m">{n.text}</div>}
      <div className="vs-notice-of">
        <span>{n.link && n.link.type !== 'none' ? (n.link.label || 'Details') + ' ›' : ''}</span>
        {list.length > 1 && <b role="button" tabIndex={0} onClick={(e) => { e.stopPropagation(); setIdx((i) => i + 1); }}>{(idx % list.length) + 1} of {list.length} ›</b>}
      </div>
    </div>
  );
}
